import { zodResolver } from "@hookform/resolvers/zod";
import type { StraightLineDefectDetailsData } from "@stryker/core/types/sections";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { convertLength } from "@/utils/mappings";

const desiredLengthTypes = [
  { value: "overall", title: "Overall Length" },
  { value: "inside", title: "Inside Length" },
] as const;

type DesiredLengthType = (typeof desiredLengthTypes)[number]["value"];

const straightLineDefectDetailsSchema = z
  .object({
    total_defect_length: z.union([
      z.number().lt(2147483647),
      z.string().refine((value) => !Number.isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
    ]),
    unit_for_total_defect_length: z.string(),
    desired_length_type: z.enum(["overall", "inside"]),
    desired_length: z.union([
      z.number().lt(2147483647).gt(0),
      z.string().refine((value) => !Number.isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
    ]),
    unit_for_desired_length: z.string(),
  })
  .refine(
    (data) => {
      const totalDefectLength =
        typeof data.total_defect_length === "string"
          ? Number.parseFloat(data.total_defect_length)
          : data.total_defect_length;
      const desiredOverallLength =
        typeof data.desired_length === "string" ? Number.parseFloat(data.desired_length) : data.desired_length;

      // Skip validation if either value is invalid
      if (Number.isNaN(totalDefectLength) || Number.isNaN(desiredOverallLength)) {
        return true;
      }

      // Convert both values to the same unit (inches) for comparison
      const totalInInches = convertLength(totalDefectLength, data.unit_for_total_defect_length, "inches");
      const desiredInInches = convertLength(desiredOverallLength, data.unit_for_desired_length, "inches");

      return desiredInInches >= totalInInches;
    },
    {
      message: "Desired length must be greater than or equal to total defect length",
      path: ["desired_length"],
    }
  );

type StraightLineDefectDetailsSchema = z.infer<typeof straightLineDefectDetailsSchema>;

interface StraightLineDefectDetailsProps {
  onSave: (data: StraightLineDefectDetailsData) => void;
}

export function StraightLineDefectDetails({ onSave }: StraightLineDefectDetailsProps) {
  const { sections } = useCUMechanicalJobStore();
  const savedDefectDetails = sections.defectDetails;
  const { measureUnitOptions } = useConfigOptions();

  const form = useForm<StraightLineDefectDetailsSchema>({
    resolver: zodResolver(straightLineDefectDetailsSchema),
    defaultValues: (() => {
      const data = savedDefectDetails.data as StraightLineDefectDetailsData as any;
      const first = Array.isArray(data?.defects) && data.defects.length > 0 ? data.defects[0] : undefined;
      const hasDesiredInsideLength =
        first?.desired_inside_length !== undefined && first?.desired_inside_length !== null
          ? true
          : data?.desired_inside_length !== undefined && data?.desired_inside_length !== null;
      const total_defect_length = first?.total_defect_length ?? data?.total_defect_length ?? "";
      const unit_for_total_defect_length =
        first?.unit_for_total_defect_length ?? data?.unit_for_total_defect_length ?? "Inches";
      const desired_length = hasDesiredInsideLength
        ? (first?.desired_inside_length ?? data?.desired_inside_length ?? "")
        : (first?.desired_overall_length ?? data?.desired_overall_length ?? "");
      const unit_for_desired_length =
        first?.unit_for_desired_overall_length ??
        data?.unit_for_desired_overall_length ??
        first?.unit_for_desired_inside_length ??
        data?.unit_for_desired_inside_length ??
        "Inches";
      return {
        total_defect_length,
        unit_for_total_defect_length,
        desired_length_type: hasDesiredInsideLength ? "inside" : "overall",
        desired_length,
        unit_for_desired_length,
      };
    })(),
  });

  const handleSave = (payload: StraightLineDefectDetailsSchema) => {
    const saved = savedDefectDetails.data as StraightLineDefectDetailsData as any;
    const defectPayload =
      payload.desired_length_type === "inside"
        ? {
            total_defect_length: payload.total_defect_length,
            unit_for_total_defect_length: payload.unit_for_total_defect_length,
            desired_inside_length: payload.desired_length,
            unit_for_desired_overall_length: payload.unit_for_desired_length,
          }
        : {
            total_defect_length: payload.total_defect_length,
            unit_for_total_defect_length: payload.unit_for_total_defect_length,
            desired_overall_length: payload.desired_length,
            unit_for_desired_overall_length: payload.unit_for_desired_length,
          };
    const data: StraightLineDefectDetailsData = {
      defects: [defectPayload],
      repair_length_options: saved?.repair_length_options || [],
      repair_length_repair_kit_sizes: saved?.repair_length_repair_kit_sizes || [],
    };
    onSave(data);
  };

  const desiredLengthType = form.watch("desired_length_type");
  const desiredLengthLabel = desiredLengthType === "inside" ? "Desired Inside Length" : "Desired Overall Length";
  const desiredLengthPlaceholder =
    desiredLengthType === "inside" ? "Enter desired inside length" : "Enter desired overall length";

  return (
    <div className="space-y-6">
      <Typography variant="text-xl">Straight Line Defect Details</Typography>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="total_defect_length"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Defect Length</FormLabel>
                  <FormControl>
                    <TextInput type="number" step={inputStep} placeholder="Enter total defect length" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unit_for_total_defect_length"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {measureUnitOptions.map((unit, index) => (
                        <SelectItem key={`unit-1-${unit.value}-${index}`} value={unit.value}>
                          {unit.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="desired_length_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Length Type</FormLabel>
                  <Select
                    onValueChange={(value: DesiredLengthType) => field.onChange(value)}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select length type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {desiredLengthTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="desired_length"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{desiredLengthLabel}</FormLabel>
                  <FormControl>
                    <TextInput type="number" step={inputStep} placeholder={desiredLengthPlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unit_for_desired_length"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {measureUnitOptions.map((unit, index) => (
                        <SelectItem key={`unit-2-${unit.value}-${index}`} value={unit.value}>
                          {unit.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button type="submit" className="ml-auto">
            Save
          </Button>
        </form>
      </Form>
    </div>
  );
}
